import { useCallback, useEffect, useMemo, useState } from "react";
import { getMysqlTables, type MysqlTablesStats } from "@admin/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import { Button } from "@admin/components/ui/button";
import { formatBytesShort, formatDate } from "@admin/lib/utils";

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-fg">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

type SortKey =
  | "tableName"
  | "rowEstimate"
  | "dataBytes"
  | "indexBytes"
  | "totalBytes"
  | "engine"
  | "avgRowLengthBytes"
  | "autoIncrement"
  | "updatedAt";

type SortDir = "asc" | "desc";

function compareValues(a: string | number | null, b: string | number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

export default function MysqlTablesPage() {
  const [stats, setStats] = useState<MysqlTablesStats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>("totalBytes");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    getMysqlTables()
      .then(setStats)
      .catch(() => setErr("Could not load MySQL table stats"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sortedTables = useMemo(() => {
    if (!stats) return [];
    const rows = [...stats.tables];
    rows.sort((left, right) => {
      const cmp = compareValues(left[sortKey], right[sortKey]);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [stats, sortKey, sortDir]);

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "tableName" || key === "engine" ? "asc" : "desc");
  };

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ^" : " v";
  };

  if (err) {
    return (
      <div className="space-y-4">
        <p className="text-danger">{err}</p>
        <Button variant="outline" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }

  if (!stats && loading) {
    return <p className="text-muted-fg">Loading...</p>;
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Database tables</h1>
          <p className="mt-1 text-sm text-muted-fg">
            Row counts are InnoDB estimates from information_schema (not exact COUNT(*)). Sizes
            include data and index bytes on disk. Schema: {stats.schemaName}. Captured{" "}
            {formatDate(stats.capturedAt)}.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          Reload
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Tables" value={stats.totals.tableCount} />
        <StatCard label="Total rows (estimate)" value={stats.totals.rowEstimate.toLocaleString()} />
        <StatCard label="Data size" value={formatBytesShort(stats.totals.dataBytes)} />
        <StatCard label="Index size" value={formatBytesShort(stats.totals.indexBytes)} />
        <StatCard label="Total size" value={formatBytesShort(stats.totals.totalBytes)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-table stats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-fg">
                  <th className="pb-2 pr-4 font-medium">
                    <button type="button" className="hover:text-fg" onClick={() => onSort("tableName")}>
                      Table{sortIndicator("tableName")}
                    </button>
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    <button type="button" className="hover:text-fg" onClick={() => onSort("rowEstimate")}>
                      Rows{sortIndicator("rowEstimate")}
                    </button>
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    <button type="button" className="hover:text-fg" onClick={() => onSort("dataBytes")}>
                      Data{sortIndicator("dataBytes")}
                    </button>
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    <button type="button" className="hover:text-fg" onClick={() => onSort("indexBytes")}>
                      Index{sortIndicator("indexBytes")}
                    </button>
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    <button type="button" className="hover:text-fg" onClick={() => onSort("totalBytes")}>
                      Total{sortIndicator("totalBytes")}
                    </button>
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    <button type="button" className="hover:text-fg" onClick={() => onSort("engine")}>
                      Engine{sortIndicator("engine")}
                    </button>
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    <button
                      type="button"
                      className="hover:text-fg"
                      onClick={() => onSort("avgRowLengthBytes")}
                    >
                      Avg row{sortIndicator("avgRowLengthBytes")}
                    </button>
                  </th>
                  <th className="pb-2 pr-4 font-medium">
                    <button
                      type="button"
                      className="hover:text-fg"
                      onClick={() => onSort("autoIncrement")}
                    >
                      Auto-inc{sortIndicator("autoIncrement")}
                    </button>
                  </th>
                  <th className="pb-2 font-medium">
                    <button type="button" className="hover:text-fg" onClick={() => onSort("updatedAt")}>
                      Updated{sortIndicator("updatedAt")}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTables.map((row) => (
                  <tr key={row.tableName} className="border-b border-border/60">
                    <td className="py-2 pr-4 font-mono text-xs">{row.tableName}</td>
                    <td className="py-2 pr-4 tabular-nums">{row.rowEstimate.toLocaleString()}</td>
                    <td className="py-2 pr-4 tabular-nums">{formatBytesShort(row.dataBytes)}</td>
                    <td className="py-2 pr-4 tabular-nums">{formatBytesShort(row.indexBytes)}</td>
                    <td className="py-2 pr-4 tabular-nums font-medium">
                      {formatBytesShort(row.totalBytes)}
                    </td>
                    <td className="py-2 pr-4">{row.engine ?? "-"}</td>
                    <td className="py-2 pr-4 tabular-nums">
                      {row.avgRowLengthBytes != null ? formatBytesShort(row.avgRowLengthBytes) : "-"}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {row.autoIncrement != null ? row.autoIncrement.toLocaleString() : "-"}
                    </td>
                    <td className="py-2 tabular-nums">
                      {row.updatedAt ? formatDate(row.updatedAt) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
